import axios from 'axios';
import { API_BASE_URL } from '../config/api';

class CompetitorAnalysisService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add token to requests
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('googleAccessToken') || sessionStorage.getItem('googleAccessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  /**
   * Save competitor analysis results to database
   */
  async saveAnalysis(data) {
    try {
      const response = await this.api.post('/competitor-analysis/save', data);
      return response.data;
    } catch (error) {
      console.error('[CompetitorAnalysisService] Error saving analysis:', error);
      throw error;
    }
  }

  /**
   * Get competitor analysis history for a user
   */
  async getHistory(userId, params = {}) {
    try {
      const response = await this.api.get(`/competitor-analysis/history/${userId}`, { params });
      return response.data;
    } catch (error) {
      console.error('[CompetitorAnalysisService] Error fetching history:', error);
      throw error;
    }
  }

  /**
   * Get competitor analysis by keyword
   */
  async getByKeyword(userId, keyword) {
    try {
      const response = await this.api.get(`/competitor-analysis/keyword/${userId}/${encodeURIComponent(keyword)}`);
      return response.data;
    } catch (error) {
      console.error('[CompetitorAnalysisService] Error fetching by keyword:', error);
      throw error;
    }
  }

  /**
   * Get latest competitor analysis for a business
   */
  async getLatest(userId, businessName) {
    try {
      const response = await this.api.get(`/competitor-analysis/latest/${userId}/${encodeURIComponent(businessName)}`);
      return response.data;
    } catch (error) {
      console.error('[CompetitorAnalysisService] Error fetching latest:', error);
      throw error;
    }
  }

  /**
   * Get latest competitor analysis by location_id (more reliable)
   * Uses user_id + location_id for accurate lookup when same location is shared by multiple users
   */
  async getLatestByLocation(userId, locationId) {
    try {
      const response = await this.api.get(`/competitor-analysis/latest-by-location/${userId}/${encodeURIComponent(locationId)}`);
      return response.data;
    } catch (error) {
      console.error('[CompetitorAnalysisService] Error fetching latest by location:', error);
      throw error;
    }
  }

  /**
   * Get a specific competitor analysis by ID (for admin viewing stored heatmaps)
   */
  async getById(analysisId) {
    try {
      const response = await this.api.get(`/competitor-analysis/by-id/${analysisId}`);
      return response.data;
    } catch (error) {
      console.error('[CompetitorAnalysisService] Error fetching by ID:', error);
      throw error;
    }
  }

  /**
   * Get competitor analysis statistics
   */
  async getStatistics(userId, params = {}) {
    try {
      const response = await this.api.get(`/competitor-analysis/statistics/${userId}`, { params });
      return response.data;
    } catch (error) {
      console.error('[CompetitorAnalysisService] Error fetching statistics:', error);
      throw error;
    }
  }

  /**
   * Get ranking trends over time for a keyword
   */
  async getRankingTrends(userId, keyword, days = 30) {
    try {
      const response = await this.api.get(`/competitor-analysis/trends/${userId}/${encodeURIComponent(keyword)}`, {
        params: { days }
      });
      return response.data;
    } catch (error) {
      console.error('[CompetitorAnalysisService] Error fetching trends:', error);
      throw error;
    }
  }

  /**
   * Delete competitor analysis record
   */
  async deleteAnalysis(id) {
    try {
      const response = await this.api.delete(`/competitor-analysis/${id}`);
      return response.data;
    } catch (error) {
      console.error('[CompetitorAnalysisService] Error deleting analysis:', error);
      throw error;
    }
  }
}

export default new CompetitorAnalysisService();
